// 〔＋ 給存取權〕精靈（SPEC #vms-web-787 IAM-11、#org-tree-ui 第 2 點；VMS UX 評審 2026-10-13 P0-9）：
//   ① 給誰：本組織成員／成員群組／另一個組織（委派代碼 → 新增委派精靈；分享請求代碼 → 分享給組織）／組織外的個人（email = 分享）
//   ② 哪裡：結構樹挑節點或戶（節點含子場域）；組織外的個人挑一戶與設備
//   ③ 能做什麼：大按鈕「管理這個場域（節點管理員）」「看與操作（操作員）」「只看（觀看者）」「安裝調校（安裝技師）」「自訂…」，「下載 / 匯出錄影」另勾
//   ④ 多久：永久／N 天（最長 90）／只在某些時段 → 摘要一句 →〔給存取權（需要重新驗證）〕
// 寫入一律用原本各來源的 API（IAM-11 驗收：產生的紀錄跟原本各頁新增的一樣）：
//   成員 / 群組 × 節點 → 節點指派 POST …/nodes/{node}/assignments；成員 × 戶 → 場域授權 POST …/nodes/{node}/grants（mode all / list，最長 90 天）；
//   組織外的個人 → 分享 POST /v1/homes/{home}/shares。自我授權（對象是自己或含自己的群組）照組織樹第 2 點：理由必填、黃框、主按鈕「指派自己」。
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { isKinioError } from '@kinio/web-sdk';
import { Dialog } from '../../components/Dialog';
import { orgApi, PERSONAL_SHARE_ACTIONS, PERSONAL_SHARE_DEFAULT, shareApi, type MyOrg, type ScopeHome, type ShareTarget } from '../../lib/planeApi';
import { GRANT_ACTIONS, MAINTAINER_GRANT_ACTIONS, treeApi, type NodeRole, type OrgTreeItem, type Subtree } from '../../lib/orgTreeApi';
import { actionWords, roleActions } from '../../lib/orgTreeUi';
import { useHomeLabel, useScope } from '../../lib/scope';
import { StructureTree, rootIdOf, useRootLevel } from '../orgtree/StructureTree';
import { selfNotifyKey, useMemberGroups, useOrgMembers, useRoleTemplates } from '../orgtree/hooks';
import { otErr } from '../orgtree/otErr';
import { DelegationWizard } from '../orgtree/DelegationWizard';
import { memberName } from './MembersTab';
import { dupAssignmentText } from './grantConflict';
import { AcceptOrgDialog, DevicePicker, scopeLabel, sharingHomes, useHomeDevices } from './SharesTab';

export type WhoKind = 'member' | 'group' | 'org' | 'person';
export interface GrantPreset {
  node?: { node_id: string; name: string; subtree?: Subtree; isRoot?: boolean };
  principal?: { kind: 'user' | 'group'; id: string };
}

const EMAIL_RE = /.+@.+\..+/;

type Where = { kind: 'node'; node_id: string; name: string; isRoot: boolean } | { kind: 'home'; home_id: string; name: string; node_id: string; devices?: number };
type Term = 'forever' | 'days' | 'window';

/** 場域層角色 → 場域授權的系統範本（範圍是單一戶時寫成場域授權）。節點管理員只給節點。 */
export const GRANT_ROLE_ID: Partial<Record<NodeRole, string>> = { operator: 'role-operator', viewer: 'role-viewer', installer: 'role-technician' };
const ROLE_CHOICES: NodeRole[] = ['node_admin', 'operator', 'viewer', 'installer', 'maintainer', 'custom'];
const MAX_DAYS = 90;

/** 步驟能不能往下（純函式，vitest 用）。 */
export function canNext(s: {
  step: number;
  kind: WhoKind;
  who: string;
  email: string;
  where: Where | null;
  role: NodeRole;
  tpl: string;
  picked: string[];
  scopes: string[];
  deviceMode: 'all' | 'list';
}): boolean {
  if (s.step === 0) return s.kind === 'person' ? EMAIL_RE.test(s.email.trim()) : s.kind === 'org' ? false : !!s.who;
  if (s.step === 1) {
    if (!s.where) return false;
    if (s.kind === 'person') return s.where.kind === 'home' && s.picked.length > 0;
    if (s.kind === 'group' && s.where.kind !== 'node') return false;
    return s.where.kind === 'node' || s.deviceMode === 'all' || s.picked.length > 0;
  }
  if (s.step === 2) {
    if (s.kind === 'person') return s.scopes.length > 0;
    if (s.role === 'custom') return !!s.tpl;
    if (s.role === 'node_admin') return s.where?.kind === 'node';
    return true;
  }
  return true;
}

export function GrantWizard({ org, open, preset, close, done }: { org: MyOrg; open: boolean; preset?: GrantPreset; close(): void; done(msg: string): void }) {
  const { t } = useTranslation();
  const { homes } = useScope();
  // 家戶下拉一律寫路徑「LivingStyle › lstw › rd」（第八輪 B8-04：兩個「rd」分不出來）
  const homeLabel = useHomeLabel();
  const team = org.kind === 'team';
  const members = useOrgMembers(org, open);
  const groups = useMemberGroups(org, open && team);
  const templates = useRoleTemplates(org, open);
  const root = useRootLevel(org);
  const rootId = rootIdOf(root.data);
  const [step, setStep] = useState(0);
  const [kind, setKind] = useState<WhoKind>('member');
  const [who, setWho] = useState('');
  const [filter, setFilter] = useState('');
  const [email, setEmail] = useState('');
  // 按了〔下一步〕但 email 空白 / 不像 email：提示改錯誤樣式並聚焦（第十一輪 B11-07）
  const [emailTried, setEmailTried] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);
  const [orgMode, setOrgMode] = useState<'' | 'delegation' | 'share'>('');
  const [shareHomeId, setShareHomeId] = useState('');
  const [where, setWhere] = useState<Where | null>(null);
  const [deviceMode, setDeviceMode] = useState<'all' | 'list'>('all');
  const [picked, setPicked] = useState<string[]>([]);
  const [role, setRole] = useState<NodeRole>('viewer');
  const [tpl, setTpl] = useState('');
  const [exportToo, setExportToo] = useState(false);
  const [scopes, setScopes] = useState<string[]>(PERSONAL_SHARE_DEFAULT);
  const [term, setTerm] = useState<Term>('forever');
  const [days, setDays] = useState(30);
  const [win, setWin] = useState({ from: '19:00', to: '07:00' });
  const [reason, setReason] = useState('');
  const [needReason, setNeedReason] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [handoff, setHandoff] = useState<'delegation' | 'share' | null>(null);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setKind(preset?.principal?.kind === 'group' ? 'group' : 'member');
    setWho(preset?.principal ? `${preset.principal.kind}:${preset.principal.id}` : '');
    setFilter('');
    setEmail('');
    setEmailTried(false);
    setOrgMode('');
    setShareHomeId('');
    setWhere(preset?.node ? { kind: 'node', node_id: preset.node.node_id, name: preset.node.name, isRoot: preset.node.isRoot ?? false } : null);
    setDeviceMode('all');
    setPicked([]);
    setRole('viewer');
    setTpl('');
    setExportToo(false);
    setScopes(PERSONAL_SHARE_DEFAULT);
    setTerm('forever');
    setDays(30);
    setReason('');
    setNeedReason(false);
    setErr(null);
    setHandoff(null);
  }, [open, preset]);

  const nodeId = where?.kind === 'node' ? where.node_id : '';
  const nodeQ = useQuery({ queryKey: ['ot', org.org_id, 'node', nodeId], queryFn: () => treeApi.node(org, nodeId), enabled: open && !!nodeId, retry: false });
  const shareable = useMemo(() => sharingHomes(homes).filter((h) => h.org_id === org.org_id), [homes, org.org_id]);
  // 組織外的個人：家戶與設備清單要走那個家戶的 façade（我得有那戶的管理權）
  const personHome: ScopeHome | undefined = where?.kind === 'home' ? (homes.find((h) => h.home_id === where.home_id) as ScopeHome | undefined) : undefined;

  const me = (members.data ?? []).find((m) => m.is_me);
  const [pk, pid] = who.split(':') as ['user' | 'group', string];
  const group = pk === 'group' ? (groups.data ?? []).find((g) => g.group_id === pid) : undefined;
  const isSelf = !!me && ((pk === 'user' && pid === me.user_id) || (pk === 'group' && !!group?.members?.some((m) => m.user_id === me.user_id)));
  const whoName = kind === 'person' ? email.trim() : pk === 'group' ? (group?.name ?? '') : memberName((members.data ?? []).find((m) => m.user_id === pid), '');
  const tplActions = (templates.data ?? []).find((r) => r.role_id === tpl)?.actions ?? [];
  const extra = exportToo ? ['recording.export'] : [];
  const siteGrant = kind === 'member' && where?.kind === 'home';
  // 單台（site 授權）只收 GRANT_ACTIONS：「看與操作」的收警報不能放進 grant（不濾掉會 400 BAD_ACTION，vms#32）
  const actions = kind === 'person' ? scopes : roleActions(role, extra, tplActions).filter((a) => !siteGrant || GRANT_ACTIONS.includes(a));
  const termOk = termChoices(kind, where?.kind);
  const effTerm: Term = termOk.includes(term) ? term : 'days';
  const wherePath =
    where?.kind === 'node'
      ? where.isRoot || where.node_id === rootId
        ? t('ot.whole_org_name', { org: org.display_name })
        : [org.display_name, ...(nodeQ.data?.path ?? []).filter((p) => p.node_id !== rootId).map((p) => p.name), where.name].join(' › ')
      : where?.kind === 'home'
        ? where.name
        : '';
  const sub = where?.kind === 'node' ? (nodeQ.data?.subtree ?? (where.isRoot ? root.data?.root?.subtree : undefined) ?? preset?.node?.subtree) : undefined;
  const size =
    where?.kind === 'node' && sub
      ? t('iam.size_node', { homes: sub.homes, devices: sub.devices })
      : where?.kind === 'home'
        ? kind === 'person' || deviceMode === 'list'
          ? t('iam.size_list', { n: picked.length })
          : t('iam.size_home', { count: where.devices ?? 0 })
        : '';
  const termText =
    effTerm === 'forever'
      ? t('ot.forever')
      : effTerm === 'days'
        ? t('iam.term_days', { count: days })
        : t('iam.term_window', { from: win.from, to: win.to });
  const verb = kind === 'person' ? t('iam.verb_share', { what: actionWords(t, scopes) }) : role === 'custom' ? t('iam.verb_custom', { name: (templates.data ?? []).find((x) => x.role_id === tpl)?.name ?? '' }) : t('iam.verb_' + role);

  async function submit() {
    if (!where) return;
    if ((isSelf || needReason) && !reason.trim()) {
      setNeedReason(true);
      setErr(t('org.err_REASON_REQUIRED'));
      return;
    }
    setBusy(true);
    setErr(null);
    const expires = effTerm === 'days' ? new Date(Date.now() + days * 86400_000).toISOString() : null;
    const schedule = effTerm === 'window' ? { tz: Intl.DateTimeFormat().resolvedOptions().timeZone, windows: [{ days: [1, 2, 3, 4, 5, 6, 7], from: win.from, to: win.to }] } : null;
    try {
      if (kind === 'person') {
        if (!personHome) throw new Error('home');
        await shareApi.toPerson(personHome.endpoints.api, personHome.home_id, {
          email: email.trim(),
          targets: picked.map((ref): ShareTarget => ({ device_ref: ref })),
          scopes,
          expires_at: expires ?? undefined,
        });
        done(t('iam.done_share', { email: email.trim() }));
      } else if (where.kind === 'node') {
        await treeApi.createAssignment(org, where.node_id, {
          principal_kind: pk,
          principal_id: pid,
          role,
          ...(role === 'custom' && tpl ? { role_template_ref: tpl } : {}),
          extra_actions: extra,
          expires_at: expires,
          schedule,
          ...(reason.trim() ? { reason: reason.trim() } : {}),
        });
        done(t('iam.done', { who: whoName, where: wherePath }));
      } else {
        const rid = GRANT_ROLE_ID[role];
        await orgApi.createGrant(org, where.node_id, {
          home_id: where.home_id,
          user_id: pid,
          // 維運員給單台：site 授權用固定的維運員組合（看設備 + 維運 + KMS 監控；平台只收這一組帶 device.maintain）
          ...(role === 'maintainer' ? { actions: MAINTAINER_GRANT_ACTIONS } : rid && !extra.length ? { role_id: rid } : { actions }),
          resource_scope: deviceMode === 'all' ? { mode: 'all' } : { mode: 'list', devices: picked.map((ref) => ({ device_ref: ref })) },
          include_descendants: false,
          expires_at: expires ?? new Date(Date.now() + MAX_DAYS * 86400_000).toISOString(),
        });
        done(t('iam.done', { who: whoName, where: wherePath }));
      }
      close();
    } catch (e) {
      if (isKinioError(e) && e.code === 'REASON_REQUIRED') {
        setNeedReason(true);
        setErr(t('ot.reason_needed_self'));
      } else if (isKinioError(e) && e.code === 'CONFLICT' && e.detail?.assignment_id && where.kind === 'node') {
        // 這個對象在這個節點已有指派：寫出是哪一筆、去哪裡改（vms#32）
        const id = String(e.detail.assignment_id);
        const acc = await treeApi.access(org, where.node_id).catch(() => null);
        setErr(dupAssignmentText(t, acc?.assignments.find((a) => a.assignment_id === id), wherePath));
      } else setErr(otErr(t, e));
    } finally {
      setBusy(false);
    }
  }

  if (handoff === 'delegation') return <DelegationWizard org={org} close={close} done={(m) => (done(m), close())} />;
  if (handoff === 'share') {
    const h = shareable.find((x) => x.home_id === shareHomeId);
    if (h) return <AcceptOrgDialog home={h} open close={close} done={() => done(t('iam.done_org_share'))} />;
  }

  const steps = [t('iam.step_who'), t('iam.step_where'), t('iam.step_what'), t('iam.step_term')];
  const people = (members.data ?? []).filter((m) => m.status !== 'suspended' && (!filter.trim() || (memberName(m) + ' ' + (m.email ?? '')).toLowerCase().includes(filter.trim().toLowerCase())));
  const gs = (groups.data ?? []).filter((g) => !filter.trim() || g.name.toLowerCase().includes(filter.trim().toLowerCase()));
  const notify = selfNotifyKey(org, members.data);
  const next = canNext({ step, kind, who, email, where, role, tpl, picked, scopes, deviceMode });
  const emailOk = EMAIL_RE.test(email.trim());

  return (
    <Dialog open={open} title={t('iam.grant_title')} onClose={close} wide>
      <div className="form" data-testid="grant-wizard" data-step={step}>
        <ol className="ot-steps" aria-label={t('ot.steps')}>
          {steps.map((s, i) => (
            <li key={s} aria-current={i === step ? 'step' : undefined} className={i === step ? 'on' : i < step ? 'done' : ''}>
              {s}
            </li>
          ))}
        </ol>

        {step === 0 && (
          <>
            <div className="iam-kinds" role="radiogroup" aria-label={t('iam.step_who')}>
              {(['member', ...(team ? ['group'] : []), ...(team ? ['org'] : []), 'person'] as WhoKind[]).map((k) => (
                <label key={k} className={'iam-kind' + (kind === k ? ' on' : '')}>
                  <input type="radio" name="iam-kind" checked={kind === k} onChange={() => (setKind(k), setWho(''), setFilter(''))} />
                  <strong>{t('iam.kind_' + k)}</strong>
                  <span className="muted small">{t('iam.kind_' + k + '_desc')}</span>
                </label>
              ))}
            </div>
            {(kind === 'member' || kind === 'group') && (
              <>
                <input className="input" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder={t('ot.who_search')} aria-label={t('ot.who_search')} />
                <div className="ot-pick-list" role="listbox" aria-label={t('iam.step_who')}>
                  {kind === 'group' &&
                    gs.map((g) => (
                      <label key={g.group_id} className="ot-pick">
                        <input type="radio" name="iam-who" checked={who === 'group:' + g.group_id} onChange={() => setWho('group:' + g.group_id)} />▦ {g.name}{' '}
                        <span className="muted small">{t('ot.member_group_n', { n: g.member_count })}</span>
                      </label>
                    ))}
                  {kind === 'member' &&
                    people.map((m) => (
                      <label key={m.user_id} className="ot-pick">
                        <input type="radio" name="iam-who" checked={who === 'user:' + m.user_id} onChange={() => setWho('user:' + m.user_id)} />
                        {memberName(m)}
                        {m.is_me && <span className="badge">{t('ot.you')}</span>}
                        {m.email && m.display_name && <span className="muted small"> {m.email}</span>}
                      </label>
                    ))}
                </div>
              </>
            )}
            {kind === 'person' && (
              <label className="field">
                <span>{t('auth.email')}</span>
                <input
                  ref={emailRef}
                  className="input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  aria-label={t('auth.email')}
                  aria-invalid={!emailOk || undefined}
                  aria-describedby="iam-email-need"
                />
                {/* email 空白或不像 email：說要填什麼（第八輪 B8-04）；按了〔下一步〕還是空白 → 錯誤樣式（第十一輪 B11-07） */}
                {!emailOk && (
                  <span id="iam-email-need" className={emailTried ? 'small error' : 'small'} role={emailTried ? 'alert' : 'status'} data-testid="iam-email-need" data-error={emailTried ? '1' : undefined}>
                    {t('iam.email_need')}
                  </span>
                )}
                <span className="small muted">{t('iam.person_hint')}</span>
              </label>
            )}
            {kind === 'org' && (
              <div className="form" data-testid="iam-org">
                <label className="ot-pick">
                  <input type="radio" name="iam-org" checked={orgMode === 'delegation'} onChange={() => setOrgMode('delegation')} />
                  <strong>{t('iam.org_delegation')}</strong> <span className="muted small">{t('iam.org_delegation_desc')}</span>
                </label>
                <label className="ot-pick">
                  <input type="radio" name="iam-org" checked={orgMode === 'share'} onChange={() => setOrgMode('share')} />
                  <strong>{t('iam.org_share')}</strong> <span className="muted small">{t('iam.org_share_desc')}</span>
                </label>
                {orgMode === 'share' && (
                  <label className="field">
                    <span>{t('people.home')}</span>
                    <select className="input" value={shareHomeId} onChange={(e) => setShareHomeId(e.target.value)}>
                      <option value="">—</option>
                      {shareable.map((h) => (
                        <option key={h.home_id} value={h.home_id}>
                          {homeLabel(h)}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
            )}
          </>
        )}

        {step === 1 && kind !== 'person' && (
          <>
            <p className="muted small">{kind === 'group' ? t('iam.where_hint_group') : t('iam.where_hint')}</p>
            <StructureTree
              org={org}
              selected={where?.kind === 'node' ? where.node_id : undefined}
              selectedHome={where?.kind === 'home' ? where.home_id : undefined}
              onSelect={(n) => (setWhere({ kind: 'node', node_id: n.node_id, name: n.name, isRoot: n.isRoot }), setPicked([]))}
              onPickHome={
                kind === 'member'
                  ? (h: OrgTreeItem) => (setWhere({ kind: 'home', home_id: h.home_id!, name: h.name, node_id: h.parent_node_id ?? rootId, devices: h.devices }), setPicked([]), setDeviceMode('all'))
                  : undefined
              }
              showLock={false}
              showUnassigned={false}
            />
            {where && (
              <p className="small" data-testid="iam-where">
                {t('iam.picked', { where: wherePath })}
                {size && ' · ' + (where.kind === 'node' ? t('iam.size_struct', { x: size }) : size)}
              </p>
            )}
            {where?.kind === 'home' && (
              <fieldset className="field">
                <legend>{t('people.scope')}</legend>
                <label className="check">
                  <input type="radio" name="iam-dev" checked={deviceMode === 'all'} onChange={() => setDeviceMode('all')} />
                  <span>{t('iam.devices_all')}</span>
                </label>
                <label className="check">
                  <input type="radio" name="iam-dev" checked={deviceMode === 'list'} disabled={!personHome} onChange={() => setDeviceMode('list')} />
                  <span>{personHome ? t('iam.devices_list') : t('iam.devices_list_na')}</span>
                </label>
                {deviceMode === 'list' && personHome && <HomeDevicePicker home={personHome} picked={picked} setPicked={setPicked} grant />}
              </fieldset>
            )}
          </>
        )}
        {step === 1 && kind === 'person' && (
          <>
            <label className="field">
              <span>{t('people.home')}</span>
              <select
                className="input"
                value={where?.kind === 'home' ? where.home_id : ''}
                onChange={(e) => {
                  const h = shareable.find((x) => x.home_id === e.target.value);
                  setWhere(h ? { kind: 'home', home_id: h.home_id, name: h.display_name ?? '', node_id: '' } : null);
                  setPicked([]);
                }}
              >
                <option value="">—</option>
                {shareable.map((h) => (
                  <option key={h.home_id} value={h.home_id}>
                    {homeLabel(h)}
                  </option>
                ))}
              </select>
              {shareable.length === 0 && <span className="small muted">{t('shr.no_homes')}</span>}
            </label>
            {personHome && (
              <fieldset className="field">
                <legend>{t('ext.devices')}</legend>
                <HomeDevicePicker home={personHome} picked={picked} setPicked={setPicked} />
              </fieldset>
            )}
          </>
        )}

        {step === 2 && kind !== 'person' && (
          <div className="iam-roles" role="radiogroup" aria-label={t('iam.step_what')}>
            {ROLE_CHOICES.filter((r) => (r !== 'node_admin' || where?.kind === 'node') && (r !== 'custom' || (templates.data?.length ?? 0) > 0)).map((r) => (
              <label key={r} className={'iam-kind' + (role === r ? ' on' : '')}>
                <input type="radio" name="iam-role" checked={role === r} onChange={() => setRole(r)} />
                <strong>{t('iam.role_' + r)}</strong>
                <span className="muted small">{t('ot.role_desc_' + r, { defaultValue: t('iam.role_custom_desc') })}</span>
              </label>
            ))}
            {role === 'custom' && (
              <select className="input" value={tpl} onChange={(e) => setTpl(e.target.value)} aria-label={t('ot.role_custom')}>
                <option value="">—</option>
                {(templates.data ?? []).map((x) => (
                  <option key={x.role_id} value={x.role_id}>
                    {x.name}
                  </option>
                ))}
              </select>
            )}
            <label className="row-inline">
              <input type="checkbox" checked={exportToo} onChange={(e) => setExportToo(e.target.checked)} />
              {t('ot.extra_recording_export')}
            </label>
          </div>
        )}
        {step === 2 && kind === 'person' && (
          <fieldset className="field">
            <legend>{t('ext.scopes')}</legend>
            <div className="check-grid">
              {PERSONAL_SHARE_ACTIONS.map((s) => (
                <label key={s} className="check">
                  <input type="checkbox" checked={scopes.includes(s)} disabled={s === 'device.read'} onChange={(e) => setScopes(e.target.checked ? [...scopes, s] : scopes.filter((x) => x !== s))} />
                  <span>{scopeLabel(t, s)}</span>
                </label>
              ))}
            </div>
            <span className="small muted">{t('shr.no_reshare')}</span>
          </fieldset>
        )}

        {step === 3 && (
          <>
            <fieldset className="field">
              <legend>{t('iam.step_term')}</legend>
              {termOk.includes('forever') && (
                <label className="check">
                  <input type="radio" name="iam-term" checked={effTerm === 'forever'} onChange={() => setTerm('forever')} />
                  <span>{t('ot.forever')}</span>
                </label>
              )}
              <label className="check">
                <input type="radio" name="iam-term" checked={effTerm === 'days'} onChange={() => setTerm('days')} />
                <span>{t('iam.term_n_days')}</span>
                <input
                  className="input"
                  type="number"
                  min={1}
                  max={MAX_DAYS}
                  style={{ width: 80 }}
                  value={days}
                  aria-label={t('iam.term_n_days')}
                  onChange={(e) => (setTerm('days'), setDays(Math.max(1, Math.min(MAX_DAYS, Number(e.target.value) || 1))))}
                />
                <span className="muted small">{t('iam.term_max')}</span>
              </label>
              {termOk.includes('window') && (
                <label className="check">
                  <input type="radio" name="iam-term" checked={effTerm === 'window'} onChange={() => setTerm('window')} />
                  <span>{t('iam.term_window_label')}</span>
                  <input className="input" type="time" style={{ width: 'auto' }} value={win.from} onChange={(e) => (setTerm('window'), setWin({ ...win, from: e.target.value }))} aria-label={t('ot.window_from')} />–
                  <input className="input" type="time" style={{ width: 'auto' }} value={win.to} onChange={(e) => (setTerm('window'), setWin({ ...win, to: e.target.value }))} aria-label={t('ot.window_to')} />
                </label>
              )}
              {siteGrant && <span className="muted small">{t('iam.site_grant_term')}</span>}
            </fieldset>
            <p className="iam-summary" data-testid="grant-summary">
              {t('iam.summary', { who: whoName, verb, where: wherePath, size: size ? t('common.paren', { x: size }) : '', term: termText })}
            </p>
            {where?.kind === 'node' && <p className="small muted">{t('ot.covers_tail')}</p>}
            {kind !== 'person' && <p className="small muted">{t('ot.effective_now', { what: actionWords(t, actions) })}</p>}
            {(isSelf || needReason) && (
              <>
                <p className="ot-warn small" data-testid="self-assign">
                  {t('ot.self_yellow', { scope: wherePath })} {t('ot.self_notify_' + notify)}
                </p>
                <label className="field">
                  <span>{t('ot.reason')} *</span>
                  <input className="input" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={200} aria-label={t('ot.reason')} />
                </label>
              </>
            )}
          </>
        )}

        {err && (
          <p className="error" role="alert">
            {err}
          </p>
        )}
        <div className="dialog-actions">
          <button className="btn" onClick={step === 0 ? close : () => setStep(step - 1)}>
            {step === 0 ? t('common.cancel') : t('common.back')}
          </button>
          {step === 0 && kind === 'org' ? (
            <button className="btn btn-primary" disabled={!orgMode || (orgMode === 'share' && !shareHomeId)} onClick={() => setHandoff(orgMode as 'delegation' | 'share')}>
              {t('ot.next')}
            </button>
          ) : step < 3 ? (
            <button
              className="btn btn-primary"
              // 組織外的個人第 1 步不停用：按下去 email 不對就提示並聚焦（第十一輪 B11-07：停用的按鈕按了沒反應）
              disabled={!next && !(step === 0 && kind === 'person')}
              onClick={() => {
                if (!next) {
                  setEmailTried(true);
                  emailRef.current?.focus();
                  return;
                }
                setStep(step + 1);
              }}
            >
              {t('ot.next')}
            </button>
          ) : (
            <button className="btn btn-primary" disabled={busy || ((isSelf || needReason) && !reason.trim())} onClick={() => void submit()} data-testid="grant-submit">
              {isSelf ? t('ot.assign_self') : t('iam.grant_submit')}
              {t('ot.reauth_suffix')}
            </button>
          )}
        </div>
      </div>
    </Dialog>
  );
}

/** 第 4 步的「多久」可選項：場域授權一定有期限（最長 90 天）；分享可以永久或到期，不分時段；節點指派三種都有。 */
export function termChoices(kind: WhoKind, whereKind: 'node' | 'home' | undefined): Term[] {
  if (kind === 'member' && whereKind === 'home') return ['days'];
  if (kind === 'person') return ['forever', 'days'];
  return ['forever', 'days', 'window'];
}

/** grant = 場域授權的設備清單（不是分享，Kinio Node / Wi-Fi 基地台照列）；其他（分享給組織外的個人）不列不能分享的種類（SPEC #org-model-shares）。 */
function HomeDevicePicker({ home, picked, setPicked, grant }: { home: ScopeHome; picked: string[]; setPicked(v: string[]): void; grant?: boolean }) {
  const list = useHomeDevices(home, true);
  return <DevicePicker list={list} picked={picked} setPicked={setPicked} all={grant} />;
}
